/* tslint:disable */
/* eslint-disable */
/**
 * Payment Service API React Query Hooks
 * Generated React Query hooks for Payment Service API
 */

import { useMutation, useQuery, useQueryClient, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import { useAuth } from '@edusense/shared-auth-core';
import { 
  PlansApi, 
  SubscriptionsApi, 
  RefundsApi,
  SubscriptionApi,
  Configuration
} from './index';

// Query Keys
export const paymentQueryKeys = {
  all: ['payment'] as const,
  plans: () => [...paymentQueryKeys.all, 'plans'] as const,
  plan: (id: string) => [...paymentQueryKeys.plans(), id] as const,
  subscriptions: () => [...paymentQueryKeys.all, 'subscriptions'] as const,
  subscription: (id: string) => [...paymentQueryKeys.subscriptions(), id] as const,
  refunds: () => [...paymentQueryKeys.all, 'refunds'] as const,
  refundEligibility: (orderId: string) => [...paymentQueryKeys.refunds(), 'eligibility', orderId] as const,
  myRefunds: () => [...paymentQueryKeys.refunds(), 'my'] as const,
} as const;

// Custom hook to create configured API instances
export function usePaymentApiInstances() {
  const { token } = useAuth();
  
  const configuration = new Configuration({
    basePath: process.env.REACT_APP_PAY_URL || 'http://localhost:8777',
    accessToken: token || undefined,
  });

  return {
    plansApi: new PlansApi(configuration),
    subscriptionsApi: new SubscriptionsApi(configuration),
    refundsApi: new RefundsApi(configuration),
    subscriptionApi: new SubscriptionApi(configuration),
  };
}

// Plans API Hooks
export function useGetPlans(
  params?: { productCode?: string; page?: string; size?: string },
  options?: UseQueryOptions<any[], AxiosError>
) {
  const { plansApi } = usePaymentApiInstances();
  
  return useQuery({
    queryKey: [...paymentQueryKeys.plans(), params],
    queryFn: () => plansApi.getPlans(params).then(response => response.data),
    ...options,
  });
}

export function useGetPlanById(
  planId: string,
  options?: UseQueryOptions<any, AxiosError>
) {
  const { plansApi } = usePaymentApiInstances();
  
  return useQuery({
    queryKey: paymentQueryKeys.plan(planId),
    queryFn: () => plansApi.getPlanById({ planId }).then(response => response.data),
    enabled: !!planId,
    ...options,
  });
}

export function useGetPlansByProductCode(
  productCode: string,
  options?: UseQueryOptions<any[], AxiosError>
) {
  const { plansApi } = usePaymentApiInstances();
  
  return useQuery({
    queryKey: [...paymentQueryKeys.plans(), 'by-product-code', productCode],
    queryFn: () => plansApi.getPlansByProductCode({ productCode }).then(response => response.data),
    enabled: !!productCode,
    ...options,
  });
}

// Subscriptions API Hooks
export function useGetSubscriptions(
  options?: UseQueryOptions<any, AxiosError>
) {
  const { subscriptionsApi } = usePaymentApiInstances();
  
  return useQuery({
    queryKey: paymentQueryKeys.subscriptions(),
    queryFn: () => subscriptionsApi.getSubscriptions().then(response => response.data),
    ...options,
  });
}

export function useGetSubscriptionById(
  subscriptionId: string,
  options?: UseQueryOptions<any, AxiosError>
) {
  const { subscriptionsApi } = usePaymentApiInstances();
  
  return useQuery({
    queryKey: paymentQueryKeys.subscription(subscriptionId),
    queryFn: () => subscriptionsApi.getSubscriptionById({ subscriptionId }).then(response => response.data),
    enabled: !!subscriptionId,
    ...options,
  });
}

export function useCancelSubscription(
  options?: UseMutationOptions<any, AxiosError, { subscriptionId: string; cancelSubscriptionRequest?: any }>
) {
  const { subscriptionsApi } = usePaymentApiInstances();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ subscriptionId, cancelSubscriptionRequest }) => 
      subscriptionsApi.cancelSubscription({ subscriptionId, cancelSubscriptionRequest }).then(response => response.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: paymentQueryKeys.subscriptions() });
      queryClient.invalidateQueries({ queryKey: paymentQueryKeys.subscription(variables.subscriptionId) });
    },
    ...options,
  });
}

export function useActivateSubscription(
  options?: UseMutationOptions<any, AxiosError, { subscriptionId: string; body?: object }>
) {
  const { subscriptionsApi } = usePaymentApiInstances();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ subscriptionId, body }) => 
      subscriptionsApi.activateSubscription({ subscriptionId, body }).then(response => response.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: paymentQueryKeys.subscriptions() });
      queryClient.invalidateQueries({ queryKey: paymentQueryKeys.subscription(variables.subscriptionId) });
    },
    ...options,
  });
}

// Refunds API Hooks
export function useCheckRefundEligibility(
  orderId: string,
  options?: UseQueryOptions<any, AxiosError>
) {
  const { refundsApi } = usePaymentApiInstances();
  
  return useQuery({
    queryKey: paymentQueryKeys.refundEligibility(orderId),
    queryFn: () => refundsApi.apiV1RefundsEligibilityOrderIdGet({ orderId }).then(response => response.data),
    enabled: !!orderId,
    ...options,
  });
}

export function useGetMyRefundRequests(
  options?: UseQueryOptions<any, AxiosError>
) {
  const { refundsApi } = usePaymentApiInstances();
  
  return useQuery({
    queryKey: paymentQueryKeys.myRefunds(),
    queryFn: () => refundsApi.apiV1RefundsMyRequestsGet().then(response => response.data),
    ...options,
  });
}

export function useRequestRefund(
  options?: UseMutationOptions<any, AxiosError, { orderId: string; reason: string }>
) {
  const { refundsApi } = usePaymentApiInstances();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ orderId, reason }) => 
      refundsApi.apiV1RefundsRequestPost({ refundRequestDto: { orderId, reason } }).then(response => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentQueryKeys.myRefunds() });
    },
    ...options,
  });
}

export function useCancelSubscriptionRefund(
  options?: UseMutationOptions<any, AxiosError, { subscriptionId: string; reason?: string }>
) {
  const { refundsApi } = usePaymentApiInstances();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ subscriptionId, reason }) => 
      refundsApi.apiV1RefundsCancelSubscriptionPost({ 
        cancelSubscriptionDto: { subscriptionId, reason } 
      }).then(response => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentQueryKeys.subscriptions() });
      queryClient.invalidateQueries({ queryKey: paymentQueryKeys.myRefunds() });
    },
    ...options,
  });
}

// Session management (SubscriptionApi)
export function useInitSubscriptionSession(
  options?: UseMutationOptions<any, AxiosError, void>
) {
  const { subscriptionApi } = usePaymentApiInstances();
  
  return useMutation({
    mutationFn: () => subscriptionApi.initSession().then(response => response.data),
    ...options,
  });
}

export function useCreateSubscriptionSession(
  options?: UseMutationOptions<any, AxiosError, {
    planId: string;
    userId: string;
    amount: number;
    productName: string;
    productCode: string;
  }>
) {
  const { subscriptionApi } = usePaymentApiInstances();
  
  return useMutation({
    mutationFn: (data) => 
      subscriptionApi.createSession({ createSessionRequest: data }).then(response => response.data),
    ...options,
  });
}

export function useChangePlan(
  options?: UseMutationOptions<any, AxiosError, {
    subscriptionId: string;
    planId: string;
    userId: string;
    amount: number;
    productName: string;
    productCode: string;
  }>
) {
  const { subscriptionApi } = usePaymentApiInstances();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ subscriptionId, ...data }) => 
      subscriptionApi.changePlan({ 
        subscriptionId, 
        planChangeRequest: data 
      }).then(response => response.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: paymentQueryKeys.subscriptions() });
      queryClient.invalidateQueries({ queryKey: paymentQueryKeys.subscription(variables.subscriptionId) });
    },
    ...options,
  });
}

// Utility hook that provides all the hooks in a single object (similar to usePaymentApi)
export function usePaymentClient() {
  const getPlansQuery = (params?: { productCode?: string; page?: string; size?: string }) => 
    useGetPlans(params);
  const getPlanByIdQuery = (planId: string) => useGetPlanById(planId);
  const getPlansByProductCodeQuery = (productCode: string) => useGetPlansByProductCode(productCode);
  
  const getSubscriptionsQuery = () => useGetSubscriptions();
  const getSubscriptionByIdQuery = (subscriptionId: string) => useGetSubscriptionById(subscriptionId);
  
  const checkRefundEligibilityQuery = (orderId: string) => useCheckRefundEligibility(orderId);
  const getMyRefundRequestsQuery = () => useGetMyRefundRequests();
  
  const cancelSubscriptionMutation = useCancelSubscription();
  const activateSubscriptionMutation = useActivateSubscription();
  const requestRefundMutation = useRequestRefund();
  const cancelSubscriptionRefundMutation = useCancelSubscriptionRefund();
  const initSubscriptionSessionMutation = useInitSubscriptionSession();
  const createSubscriptionSessionMutation = useCreateSubscriptionSession();
  const changePlanMutation = useChangePlan();
  
  return {
    // Query hooks
    plans: {
      getPlans: getPlansQuery,
      getPlanById: getPlanByIdQuery,
      getPlansByProductCode: getPlansByProductCodeQuery,
    },
    subscriptions: {
      getSubscriptions: getSubscriptionsQuery,
      getSubscriptionById: getSubscriptionByIdQuery,
      cancel: cancelSubscriptionMutation,
      activate: activateSubscriptionMutation,
      changePlan: changePlanMutation,
      initSession: initSubscriptionSessionMutation,
      createSession: createSubscriptionSessionMutation,
    },
    refunds: {
      checkEligibility: checkRefundEligibilityQuery,
      getMyRequests: getMyRefundRequestsQuery,
      request: requestRefundMutation,
      cancelSubscription: cancelSubscriptionRefundMutation,
    },
  };
}